<template>
    <div>
      <SettingHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Payment Types</h5>
          <p class="page-sub">Enable or disable payment methods accepted at checkout</p>
        </div>
      </div>
  
      <!-- Data card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading payment types...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status === 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to manage payment types.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="payment_types.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="money-bill-wave" /></div>
          <p class="state-title">No payment types found</p>
          <p class="state-sub">Payment types will appear here once configured.</p>
        </div>
  
        <!-- List -->
        <template v-else>
          <div class="list-header">
            <span class="list-header-label">Payment Method</span>
            <span class="list-header-label">Status</span>
          </div>
  
          <div
            v-for="(pt, index) in payment_types"
            :key="pt.id"
            class="payment-row"
            :class="{ 'payment-row--active': statuses[index] }"
          >
            <div class="payment-info">
              <div class="payment-icon" :class="paymentIconClass(pt.name)">
                <font-awesome-icon :icon="paymentIcon(pt.name)" />
              </div>
              <div>
                <div class="payment-name">{{ formatName(pt.name) }}</div>
                <div class="payment-desc">{{ paymentDesc(pt.name) }}</div>
              </div>
            </div>
  
            <label class="toggle-switch" :class="{ 'toggle-disabled': permission.change_payment_type != 1 }">
              <input
                type="checkbox"
                :true-value="1"
                :false-value="0"
                v-model="statuses[index]"
                :disabled="permission.change_payment_type != 1"
                @change="updatePaymentStatus(pt.id, $event)"
              />
              <span class="toggle-track"><span class="toggle-thumb"></span></span>
              <span class="toggle-label" :class="statuses[index] ? 'on' : 'off'">
                {{ statuses[index] ? 'Enabled' : 'Disabled' }}
              </span>
            </label>
          </div>
  
          <!-- Permission note -->
          <div v-if="permission.change_payment_type != 1" class="permission-note">
            <font-awesome-icon icon="info-circle" class="me-2" />
            You have view-only access. Contact your administrator to modify payment types.
          </div>
        </template>
  
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import SettingHeaderTab from './components/settingheadertab.vue';
  
  export default {
    name: 'PaymentType',
    components: { SettingHeaderTab },
  
    setup() {
      const store   = useStore();
      const toast   = useToast();
  
      const payment_types    = ref([]);
      const statuses         = ref([]);
      const loading          = ref(false);
      const permission_status = ref(null);
  
      const permission = computed(() => store.state.getCurrentUserPermission);
  
      // ── Helpers ──────────────────────────────────────────────────────
      const formatName = (name) => {
        if (!name) return '';
        return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      };
  
      const paymentIcon = (name) => {
        if (!name) return 'money-bill-wave';
        const n = name.toLowerCase();
        if (n.includes('card') || n.includes('credit') || n.includes('debit')) return 'credit-card';
        if (n.includes('cash'))   return 'money-bill-wave';
        if (n.includes('bank') || n.includes('transfer')) return 'university';
        if (n.includes('mobile') || n.includes('ussd'))   return 'mobile-alt';
        if (n.includes('cheque') || n.includes('check'))  return 'money-check';
        if (n.includes('wallet') || n.includes('online')) return 'wallet';
        return 'money-bill-wave';
      };
  
      const paymentIconClass = (name) => {
        if (!name) return 'icon--default';
        const n = name.toLowerCase();
        if (n.includes('card') || n.includes('credit') || n.includes('debit')) return 'icon--blue';
        if (n.includes('cash'))   return 'icon--green';
        if (n.includes('bank') || n.includes('transfer')) return 'icon--indigo';
        if (n.includes('mobile') || n.includes('ussd'))   return 'icon--amber';
        if (n.includes('wallet') || n.includes('online')) return 'icon--purple';
        return 'icon--default';
      };
  
      const paymentDesc = (name) => {
        if (!name) return '';
        const n = name.toLowerCase();
        if (n.includes('cash'))     return 'Accept physical cash payments';
        if (n.includes('card'))     return 'Accept credit and debit card payments';
        if (n.includes('transfer')) return 'Accept direct bank transfers';
        if (n.includes('mobile'))   return 'Accept mobile money payments';
        if (n.includes('ussd'))     return 'Accept USSD-based payments';
        if (n.includes('cheque') || n.includes('check')) return 'Accept cheque payments';
        if (n.includes('wallet'))   return 'Accept digital wallet payments';
        return 'Manage this payment method';
      };
  
      // ── Data ─────────────────────────────────────────────────────────
      const loadTypes = async () => {
        const { data } = await axios.get('/settings/payment_type');
        payment_types.value = data.data;
        statuses.value = data.data.map(p => p.is_active);
      };
  
      const getPaymentTypes = async () => {
        loading.value = true;
        try {
          await loadTypes();
        } catch (err) {
          if (err?.response?.status === 403) {
            permission_status.value = 403;
            toast.error(err.response.data.message);
          } else {
            toast.error('Could not load payment types.');
          }
        } finally {
          loading.value = false;
        }
      };
  
      const updatePaymentStatus = async (id, evt) => {
        try {
          const { data } = await axios.put('settings/payment_type/' + id, { status: evt.target.checked });
          toast.success(data.message);
        } catch (err) {
          // Revert toggle state to match server
          await loadTypes();
          const status = err.response?.status;
          if (status === 422) toast.warning('Invalid request.');
          else if (status === 403) toast.error(err.response.data.message);
          else toast.error('Could not update payment status.');
        }
      };
  
      onMounted(getPaymentTypes);
  
      return {
        loading, payment_types, statuses, permission_status,
        permission, formatName, paymentIcon, paymentIconClass, paymentDesc,
        updatePaymentStatus,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Data card ───────────────────────────────────────────────────────── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
  .state-icon--red  { background:#fff0f0; color:#e03535; }
  .state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell  { color:#4a5e78; }
  .mt-3 { margin-top:12px; }
  
  /* ── List header ─────────────────────────────────────────────────────── */
  .list-header       { display:flex; align-items:center; justify-content:space-between; padding:12px 24px; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
  .list-header-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; }
  
  /* ── Payment row ─────────────────────────────────────────────────────── */
  .payment-row { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .payment-row:last-of-type { border-bottom:none; }
  .payment-row:hover { background:#f8fafc; }
  .payment-row--active { background:#fafffe; }
  
  /* ── Payment info ────────────────────────────────────────────────────── */
  .payment-info { display:flex; align-items:center; gap:14px; }
  .payment-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .icon--blue    { background:rgba(0,74,173,.1);  color:#004AAD; }
  .icon--green   { background:#f0fdf4; color:#16a34a; }
  .icon--indigo  { background:#eef2ff; color:#4f46e5; }
  .icon--amber   { background:#fff7ed; color:#e08c00; }
  .icon--purple  { background:#faf5ff; color:#7c3aed; }
  .icon--default { background:#f4f7fb; color:#7a90b0; }
  .payment-name  { font-size:14px; font-weight:600; color:#1a2b45; margin-bottom:3px; }
  .payment-desc  { font-size:12px; color:#7a90b0; }
  
  /* ── Toggle switch ───────────────────────────────────────────────────── */
  .toggle-switch          { display:inline-flex; align-items:center; gap:10px; cursor:pointer; user-select:none; }
  .toggle-switch input    { display:none; }
  .toggle-track           { position:relative; width:44px; height:24px; border-radius:24px; background:#d1d9e8; transition:background .2s; flex-shrink:0; }
  .toggle-switch input:checked + .toggle-track { background:#16a34a; }
  .toggle-thumb           { position:absolute; top:4px; left:4px; width:16px; height:16px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.2); transition:transform .2s; }
  .toggle-switch input:checked + .toggle-track .toggle-thumb { transform:translateX(20px); }
  .toggle-label           { font-size:13px; font-weight:600; min-width:56px; }
  .toggle-label.on        { color:#16a34a; }
  .toggle-label.off       { color:#7a90b0; }
  .toggle-disabled        { opacity:.5; cursor:not-allowed; }
  
  /* ── Permission note ─────────────────────────────────────────────────── */
  .permission-note { display:flex; align-items:center; padding:14px 24px; background:#fff7ed; border-top:1px solid #fed7aa; font-size:13px; color:#e08c00; font-weight:500; }
  .me-2 { margin-right:8px; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:768px) {
    .payment-row  { flex-direction:column; align-items:flex-start; gap:14px; }
    .page-header  { flex-direction:column; }
  }
  </style>